/**
 * OnboardingTab Component
 *
 * This component provides the onboarding functionality for the admin dashboard.
 */

import React, { useState, useEffect } from 'react';
import config from '../config';
import './OnboardingTab.css';

function OnboardingTab({ getAuthHeaders }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [debugInfo, setDebugInfo] = useState({
    sessionId: localStorage.getItem('sessionId') || 'Not found',
    requests: [],
    responses: []
  });

  // Add a request to debug info
  const addRequestToDebug = (endpoint, headers) => {
    setDebugInfo(prev => ({
      ...prev,
      requests: [...prev.requests, {
        timestamp: new Date().toISOString(),
        endpoint,
        headers: JSON.stringify(headers, null, 2)
      }]
    }));
  };

  // Add a response to debug info
  const addResponseToDebug = (endpoint, status, data) => {
    setDebugInfo(prev => ({
      ...prev,
      responses: [...prev.responses, {
        timestamp: new Date().toISOString(),
        endpoint,
        status,
        data: JSON.stringify(data, null, 2)
      }]
    }));
  };

  // Fetch onboarding status from the backend
  const fetchOnboardingStatus = async () => {
    setIsLoading(true);
    setError(null);

    const headers = {
      ...getAuthHeaders(),
      'Accept': 'application/json'
    };

    // Log the request
    console.log('Fetching onboarding status with headers:', headers);
    console.log('Session ID:', localStorage.getItem('sessionId'));
    addRequestToDebug(config.endpoints.onboardingStatus, headers);

    try {
      const response = await fetch(config.endpoints.onboardingStatus, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        addResponseToDebug(config.endpoints.onboardingStatus, response.status, errorText);
        throw new Error(`Server responded with status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Onboarding status response:', data);
      addResponseToDebug(config.endpoints.onboardingStatus, response.status, data);
      setOnboardingStatus(data);
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch channels from the backend
  const fetchChannels = async () => {
    const headers = {
      ...getAuthHeaders(),
      'Accept': 'application/json'
    };

    // Log the request
    console.log('Fetching channels with headers:', headers);
    console.log('Session ID:', localStorage.getItem('sessionId'));
    addRequestToDebug(config.endpoints.adminChannels, headers);

    try {
      const response = await fetch(config.endpoints.adminChannels, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        addResponseToDebug(config.endpoints.adminChannels, response.status, errorText);
        throw new Error(`Server responded with status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Channels response:', data);
      addResponseToDebug(config.endpoints.adminChannels, response.status, data);
      
      if (data.success && data.channels) {
        setChannels(data.channels);
      } else {
        setChannels([]);
        console.warn('No channels found in response', data);
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
      setError(err.message);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchOnboardingStatus();
      await fetchChannels();
    };
    
    fetchData();

    // Update session ID if it changes
    const sessionCheckInterval = setInterval(() => {
      const currentSessionId = localStorage.getItem('sessionId');
      if (currentSessionId !== debugInfo.sessionId) {
        setDebugInfo(prev => ({
          ...prev,
          sessionId: currentSessionId || 'Not found'
        }));
      }
    }, 1000);

    return () => clearInterval(sessionCheckInterval);
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <div className="onboarding-loading">
        <div className="loader"></div>
        <p>Loading onboarding data...</p>
        <div className="debug-info">
          <p><strong>Session ID:</strong> {debugInfo.sessionId}</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="onboarding-error">
        <h3>Error loading onboarding data</h3>
        <p>{error}</p>
        <button onClick={fetchOnboardingStatus}>Retry</button>
        
        <div className="debug-info">
          <h4>Debug Information</h4>
          <p><strong>Session ID:</strong> {debugInfo.sessionId}</p>
          
          <h5>Requests</h5>
          {debugInfo.requests.map((req, index) => (
            <div key={`req-${index}`} className="debug-item">
              <p><strong>Time:</strong> {req.timestamp}</p>
              <p><strong>Endpoint:</strong> {req.endpoint}</p>
              <p><strong>Headers:</strong></p>
              <pre>{req.headers}</pre>
            </div>
          ))}
          
          <h5>Responses</h5>
          {debugInfo.responses.map((res, index) => (
            <div key={`res-${index}`} className="debug-item">
              <p><strong>Time:</strong> {res.timestamp}</p>
              <p><strong>Endpoint:</strong> {res.endpoint}</p>
              <p><strong>Status:</strong> {res.status}</p>
              <p><strong>Data:</strong></p>
              <pre>{res.data}</pre>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render onboarding status
  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <h2>WhatsApp Channel Onboarding</h2>
        <p>This page allows you to onboard WhatsApp channels to the system.</p>

        <div className="debug-info">
          <h4>Debug Information</h4>
          <p><strong>Session ID:</strong> {debugInfo.sessionId}</p>
          
          <h5>Requests</h5>
          {debugInfo.requests.map((req, index) => (
            <div key={`req-${index}`} className="debug-item">
              <p><strong>Time:</strong> {req.timestamp}</p>
              <p><strong>Endpoint:</strong> {req.endpoint}</p>
              <p><strong>Headers:</strong></p>
              <pre>{req.headers}</pre>
            </div>
          ))}
          
          <h5>Responses</h5>
          {debugInfo.responses.map((res, index) => (
            <div key={`res-${index}`} className="debug-item">
              <p><strong>Time:</strong> {res.timestamp}</p>
              <p><strong>Endpoint:</strong> {res.endpoint}</p>
              <p><strong>Status:</strong> {res.status}</p>
              <p><strong>Data:</strong></p>
              <pre>{res.data}</pre>
            </div>
          ))}
        </div>

        <div className="onboarding-status">
          <h3>Onboarding Status</h3>
          <table>
            <tbody>
              <tr>
                <td><strong>Total Channels</strong></td>
                <td>{onboardingStatus?.totalChannels || 0}</td>
              </tr>
              <tr>
                <td><strong>Total Users</strong></td>
                <td>{onboardingStatus?.totalUsers || 0}</td>
              </tr>
              <tr>
                <td><strong>Matched Channels</strong></td>
                <td>{onboardingStatus?.matched?.length || 0}</td>
              </tr>
              <tr>
                <td><strong>Unmatched Channels</strong></td>
                <td>{onboardingStatus?.unmatched?.length || 0}</td>
              </tr>
            </tbody>
          </table>
          <div className="onboarding-actions">
            <button onClick={fetchOnboardingStatus}>Refresh Status</button>
            <button className="button-primary">Start New User Onboarding</button>
          </div>
        </div>

        <div className="channels-list">
          <h3>Available Channels</h3>
          {channels.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Channel ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {channels.map(channel => (
                  <tr key={channel.id}>
                    <td>{channel.id}</td>
                    <td>{channel.name}</td>
                    <td>{channel.phone || 'None'}</td>
                    <td>
                      <span className={`status-badge status-${channel.status?.toLowerCase() || 'unknown'}`}>
                        {channel.status || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <button className="button-small">Verify</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No channels found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingTab;
